import { useMemo, useState } from 'react';
import { api, type FactorySummary, type WorkflowSummary } from '../api.ts';
import { describeTrigger, summarizeTriggers } from '../components/triggers.ts';
import type { Route } from '../routes.ts';
import { timeAgo } from './WorkView.tsx';

/**
 * The catalogue: every workflow the reader can see, as a row that says what
 * it is.
 *
 * The builder used to be the landing screen for Workflows, and it opened on
 * whichever workflow sorted first with a sidebar of names beside it. Nothing
 * listed what a workflow *is* — how it starts, how big it is, when it last
 * ran, who may edit it, which stage of which factory it stands in — so the
 * only way to answer "is this the one that runs on Monday mornings" was to
 * open each in turn. The run form had a second picker of the same names. Two
 * pickers and no list was the symptom; this is the list.
 *
 * The rows are read from the workflow list the shell already holds, which the
 * Worker now fills with the trigger, step count, last run and level per row,
 * so opening this screen costs nothing the rail did not already pay. The
 * factory stage comes from the factories the shell holds too. The editor is
 * reached only through a row — or a `/workflows/:id` link — and keeps its own
 * sidebar for switching once inside.
 */
export function CatalogueView({
  workflows,
  factories,
  go,
  onSection,
  onWorkflowsChanged,
}: {
  workflows: WorkflowSummary[];
  factories: FactorySummary[];
  go(route: Partial<Route> & { tab: Route['tab'] }, replace?: boolean): void;
  onSection(id: string): void;
  onWorkflowsChanged(): void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /*
   * Which stage a workflow serves, by id. A workflow can stand in more than
   * one factory — the same triage runs for tickets and for red builds — so the
   * cell lists every stage rather than picking one.
   */
  const stagesByWorkflow = useMemo(() => {
    const index = new Map<string, Array<{ factory: string; stage: string }>>();
    for (const factory of factories) {
      for (const stage of factory.stages) {
        const list = index.get(stage.workflowId) ?? [];
        list.push({ factory: factory.name, stage: stage.name });
        index.set(stage.workflowId, list);
      }
    }
    return index;
  }, [factories]);

  // By name rather than by the list's recency order: a catalogue is scanned
  // for a name, and the newest-edited-first order the sidebar used put the
  // workflow you touched yesterday at the top and the one you want anywhere.
  const rows = useMemo(
    () => [...workflows].sort((a, b) => a.name.localeCompare(b.name)),
    [workflows],
  );

  /**
   * A new workflow, the same document the builder's own New button creates:
   * one empty step rather than none, because a workflow with no steps opens
   * onto a blank editor with nothing to select.
   *
   * Saved first and opened second, so a save that is refused says so here —
   * under the button that was pressed — instead of opening an editor on a
   * document that does not exist.
   */
  const create = async () => {
    setBusy(true);
    setError(null);
    const id = `workflow-${Math.random().toString(36).slice(2, 7)}`;
    try {
      await api.saveWorkflow(
        id,
        {
          id,
          name: 'New workflow',
          ownerId: '',
          sharing: [],
          runtime: 'isolate',
          networkAllowlist: [],
          mcpConnections: [],
          skills: [],
          triggers: [],
          steps: [{ id: 'step-1', name: 'First step', parentId: null, level: 1, prompt: '' }],
          revision: 0,
        },
        null,
        'created from the catalogue',
      );
      onWorkflowsChanged();
      go({ tab: 'build', workflowId: id });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page catalogue">
      <header className="page-head">
        <div>
          <h1>Workflows</h1>
          <p className="page-lede">
            Every workflow you can see: how it starts, how big it is, when it last ran, and the
            factory stage it serves. Open one to edit it, or start a run from its row.
          </p>
        </div>
        <div className="row">
          <button className="ghost" onClick={() => onSection('language')}>
            Build with language
          </button>
          <button className="primary" disabled={busy} onClick={() => void create()}>
            New workflow
          </button>
        </div>
      </header>

      {error && (
        <div className="banner danger">
          <h3>That did not work</h3>
          <p style={{ margin: 0 }}>{error}</p>
        </div>
      )}

      <section className="panel">
        {rows.length === 0 ? (
          <p className="panel-quiet">Nothing shared with you yet.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="catalogue-table">
              <thead>
                <tr>
                  <th>Workflow</th>
                  <th>Starts on</th>
                  <th>Steps</th>
                  <th>Last run</th>
                  <th>You have</th>
                  <th>Stage</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((workflow) => {
                  const stages = stagesByWorkflow.get(workflow.id) ?? [];
                  const canRun = workflow.yourLevel !== 'read' ||
                    workflow.triggers.some((trigger) => trigger.type === 'manual' && trigger.triggerFromEveryone);
                  return (
                    <tr key={workflow.id} data-workflow-id={workflow.id}>
                      <td>
                        {/*
                          The name is the way in from the keyboard, and a real
                          button so it is one; the id under it because a
                          workflow is a file in a repository and the screen
                          should not hide what it is called there.
                        */}
                        <button
                          className="linkish catalogue-name"
                          onClick={() => go({ tab: 'build', workflowId: workflow.id })}
                        >
                          {workflow.name}
                        </button>
                        <div className="hint">
                          <code>{workflow.id}</code>
                        </div>
                      </td>
                      <td
                        className="catalogue-trigger"
                        title={workflow.triggers.map(describeTrigger).join('\n')}
                      >
                        {summarizeTriggers(workflow.triggers)}
                      </td>
                      <td>{workflow.stepCount}</td>
                      <td>{workflow.lastRunAt ? timeAgo(workflow.lastRunAt) : <span className="hint">never</span>}</td>
                      <td>{workflow.yourLevel}</td>
                      <td>
                        {stages.length === 0 ? (
                          <span className="hint">—</span>
                        ) : (
                          stages.map((entry) => (
                            <div key={`${entry.factory}:${entry.stage}`}>
                              {entry.stage} <span className="hint">· {entry.factory}</span>
                            </div>
                          ))
                        )}
                      </td>
                      <td className="catalogue-actions">
                        <button className="ghost" onClick={() => go({ tab: 'build', workflowId: workflow.id })}>
                          Open in builder
                        </button>
                        {/*
                          Withheld rather than refused: the API wants `edit`
                          to start a run unless a manual trigger opens it to
                          everyone, and a Run button that always 403s reads as
                          reef refusing rather than as somebody else's workflow.
                        */}
                        {canRun && (
                          <button
                            className="ghost"
                            onClick={() => go({ tab: 'build', section: 'run', workflowId: workflow.id })}
                          >
                            Run
                          </button>
                        )}
                        <button className="ghost" onClick={() => go({ tab: 'runs', workflowId: workflow.id })}>
                          Runs
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
